import BaseUrl from "./BaseUrl";

/**
 * Legacy DocChoiceWebServices path → docchoice-patientportalapi /api path.
 * Payloads are unchanged; only the URL host/path switches.
 */
const LEGACY_TO_NEW = {
  "/patientApp/patientSignIn": "/api/auth/sign-in",
  "/patientApp/patientSignUpOTP": "/api/auth/sign-up/otp",
  "/patientApp/patientSignUpReSendOTP": "/api/auth/sign-up/otp/resend",
  "/patientApp/patientSignUp": "/api/auth/sign-up",
  "/patientApp/forgotPasswordOTP": "/api/auth/forgot-password/otp",
  "/patientApp/forgotPasswordReSendOTP": "/api/auth/forgot-password/otp/resend",
  "/patientApp/forgotPasswordSignIn": "/api/auth/forgot-password/sign-in",
  "/patientApp/patientChangePassword": "/api/auth/change-password",
  "/patientPortal/isUserNameAvailable": "/api/auth/username-available",
  "/isTelegramAvailable": "/api/auth/telegram-available",
  // Legacy alias also exists: /userManagement/getClientPortalList
  "/userManagement/getClientPortalList": "/api/master/client-portal-list",
  "/patientPortal/searchSuggest": "/api/search/suggest",
  "/patientApp/getW005DefaultLocations": "/api/patient/default-locations",
  "/patientPortal/updateDefaultLocation": "/api/patient/default-location",
  "/patientApp/getPatientDashBoard": "/api/patient/dashboard",
  "/patientApp/getRegisteredUsers": "/api/patient/registered-users",
  "/patientPortal/getPatientApp": "/api/patient/profile",
  "/patientPortal/savePatientApp": "/api/patient/profile/save",
  "/caseSheet/bloodDonorReg": "/api/patient/health-card",
  "/patientApp/contactsList": "/api/patient/contacts",
  "/userManagement/addressConfiguration": "/api/master/address-configuration",
  "/patientApp/getPatientRelationList": "/api/patient/relations",
  "/patientApp/addPatientToLocation": "/api/patient/add-to-location",
  "/patientApp/savePatientRelation": "/api/patient/relations/save",
  "/patientApp/getModaluData": "/api/master/modalu-data",
  "/labs/getGenericInitialData": "/api/master/generic-initial-data",
  "/patientApp/getPaymentGatewayList": "/api/master/payment-gateways",
  "/patientPortal/getSaveC127AStatus": "/api/master/c127a-status",
  "/getPortalAppointments": "/api/appointments/list",
  "/patientPortal/getDoctorProfileDetails": "/api/appointments/doctor-profile",
  "/patientPortal/getDoctorTimeSlot": "/api/appointments/doctor-slots",
  "/patientPortal/bookAppointment": "/api/appointments/book",
  "/patientPortal/scheduleAppointment": "/api/appointments/schedule",
  "/patientPortal/cancelAppointment": "/api/appointments/cancel",
  "/patientPortal/rescheduleAppointment": "/api/appointments/reschedule",
  "/pharmacyPortal/getOrdersPayment": "/api/appointments/orders-payment",
  "/pharmacyPortal/saveOrdersPayment": "/api/appointments/orders-payment/save",
  "/patientPortal/getPatientDocuments": "/api/documents/list",
  "/patientPortal/documentupload": "/api/documents/upload",
  "/patientPortal/savePatientDocumentsRecord": "/api/documents/record",
  "/patientPortal/saveDocuments": "/api/documents/save",
  "/patientPortal/saveprescriptionupload": "/api/documents/prescription",
  "/patientPortal/getLabTests": "/api/labs/tests",
  "/patientPortal/getMyExternalLabs": "/api/labs/external",
  "/patientPortal/saveExternalLabsHDR": "/api/labs/external/save",
  "/caseSheet/printLabWorkSheet": "/api/labs/worksheet/print",
  "/labs/getLabWorkSheetLITM": "/api/labs/worksheet/items",
  "/labs/labReportsConfig": "/api/labs/reports-config",
  "/labs/ppLabWorkSheet": "/api/labs/worksheet",
  "/labs/printLabs": "/api/labs/print",
  "/pharmacyPortal/getInventoryItems": "/api/pharmacy/inventory",
  "/pharmacyPortal/getPrescriptions": "/api/pharmacy/prescriptions",
  "/pharmacyPortal/getProductOffers": "/api/pharmacy/offers",
  "/patientPortal/addToCart": "/api/pharmacy/cart/add",
  "/patientPortal/getCartItems": "/api/pharmacy/cart",
  "/pharmacyPortal/getMyOrdersHDR": "/api/pharmacy/orders",
  "/pharmacyPortal/getMyOrdersLITM": "/api/pharmacy/orders/items",
  "/patientPortal/cancelOrder": "/api/pharmacy/orders/cancel",
  "/patientPortal/saveCheckOut": "/api/pharmacy/checkout",
  "/patientPortal/getPatBillingAddresses": "/api/pharmacy/billing-addresses",
  "/patientPortal/savePatBillingAddresses": "/api/pharmacy/billing-addresses/save",
  "/caseSheet/previewOPCaseSheet": "/api/casesheet/preview",
  "/caseSheet/getVitals": "/api/casesheet/vitals",
  "/caseSheet/saveVitals": "/api/casesheet/vitals/save",
  "/doctorApp/getBubbleMasterSubscriberList": "/api/careteam/subscribers",
  "/doctorApp/saveBubbleMasterSubscriber": "/api/careteam/subscribers/save",
  "/doctorApp/getBubbleHealthChatList": "/api/careteam/chat",
  "/userManagement/saveStatusLogs": "/api/careteam/status-logs/save",
  "/userManagement/getStatusLogsList": "/api/careteam/status-logs",
};

/**
 * @param {string} legacyPath path after /DocChoiceWebServices, e.g. "/patientApp/patientSignIn"
 */
export function resolveApiUrl(legacyPath) {
  const path = legacyPath.startsWith("/") ? legacyPath : `/${legacyPath}`;
  const apiRoot = BaseUrl.patientPortalApiBaseUrl.replace(/\/$/, "");

  if (BaseUrl.usePatientPortalApi) {
    const mapped = LEGACY_TO_NEW[path];
    if (mapped) {
      return `${apiRoot}${mapped}`;
    }
    // Unmapped: keep legacy path on the new WAR (LegacyCompatController).
    console.warn("No /api mapping for", path, "- using legacy path on patientportalapi");
    return `${apiRoot}${path}`;
  }

  // Demo-style: always hit patientportalapi with legacy Jersey-style paths
  // (requires LegacyCompatController + security permit on the WAR).
  return `${apiRoot}${path}`;
}

const API_TOKEN_KEY = "patientPortalApiToken";
const API_TOKEN_EXPIRES_KEY = "patientPortalApiTokenExpiresAt";

/** Persist API bearer for the rest of the local day (matches patient session). */
export function storeApiToken(token, expiresAtMs) {
  if (!token) return;
  try {
    const exp =
      expiresAtMs ||
      (() => {
        const d = new Date();
        d.setHours(23, 59, 59, 999);
        return d.getTime();
      })();
    localStorage.setItem(API_TOKEN_KEY, token);
    localStorage.setItem(API_TOKEN_EXPIRES_KEY, String(exp));
    // Keep sessionStorage copy for older tabs mid-migration
    sessionStorage.setItem(API_TOKEN_KEY, token);
  } catch (e) {
    /* ignore */
  }
}

export function clearApiToken() {
  try {
    localStorage.removeItem(API_TOKEN_KEY);
    localStorage.removeItem(API_TOKEN_EXPIRES_KEY);
    sessionStorage.removeItem(API_TOKEN_KEY);
  } catch (e) {
    /* ignore */
  }
}

export function getApiToken() {
  try {
    const exp = parseInt(localStorage.getItem(API_TOKEN_EXPIRES_KEY) || "0", 10);
    if (exp && Date.now() > exp) {
      clearApiToken();
      return null;
    }
    return (
      localStorage.getItem(API_TOKEN_KEY) ||
      sessionStorage.getItem(API_TOKEN_KEY)
    );
  } catch (e) {
    return null;
  }
}

export { LEGACY_TO_NEW };
